import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import ChosenSchoolDetailsDisplay from '../../components/ChosenSchoolDetailsDisplay';
import PageLayout from '../../components/PageLayout/PageLayout';
import analytics from '../../lib/analytics';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import {
  selectedSchoolSelector,
  selectedCourseSelector,
  periodSelector,
  isUSRelevantSelector,
  isResidenceStateTexasSelector,
} from '../../reducers/selectors';

import '../../components/sections/provisional-offer-options/ActionButtons/index.scss';

const EligiblePage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  initialHasReadDisclosure,
  chosenSchool,
  chosenCourse,
  chosenPeriod,
  isUSRelevant,
  isResidenceStateTexas,
}) => {
  const { schoolName, universityName } = chosenSchool;
  const { label: courseName } = chosenCourse;
  const { startDate: periodStartDate, endDate: periodEndDate } = chosenPeriod;

  const [hasReadDisclosure, setHasReadDisclosure] = useState(initialHasReadDisclosure);

  const isInvalid = isUSRelevant ? !hasReadDisclosure : false;

  const handleCheckedBox = event => {
    setHasReadDisclosure(event.target.checked);
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    if (isUSRelevant) {
      const payload = payloadFactory.createAddPageDataPayload(
        'readDisclosureDocument',
        hasReadDisclosure,
      );
      dispatchAddPageData(payload);
    }

    analytics.sendEvent('Click - Continue', 'eligible');

    dispatchWizardNav({ dir: 'fwd' });
  };

  return (
    <PageLayout title="We support your chosen course and class!">
      <form onSubmit={handleSubmit}>
        <div className="row mt-4 px-2">
          <ChosenSchoolDetailsDisplay
            schoolName={schoolName}
            universityName={universityName}
            courseName={courseName}
            periodStartDate={periodStartDate}
            periodEndDate={periodEndDate}
          />
        </div>
        <div className="row">
          <div className="col col--12/16--large mt-4">
            <p>
              Your application is valid only for the specified course and cannot be changed after
              submission. Supporting documentation will be required later to confirm your admission
              to the course.
            </p>
          </div>
        </div>
        {isUSRelevant && (
          <div className="row">
            <div className="col col--12/16--large">
              <div className="row-spacer">
                <div>
                  <p>
                    To see an example of our loan offer, read our{' '}
                    {isResidenceStateTexas ? (
                      <a
                        href="https://prodigy-files.s3.amazonaws.com/files/5tSP_r02VScy8bekNEqXHA/Loan_Solicitation_Disclosure_-_no_capitalisation_-_Doc_1.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Private Education Loan Application and Solicitation Disclosure
                      </a>
                    ) : (
                      <a
                        href="https://prodigy-files.s3.amazonaws.com/files/DQIKqe3yUfX3gqf8saClYg/Loan_Solicitation_Disclosure_-_Doc_1.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Private Education Loan Application and Solicitation Disclosure
                      </a>
                    )}
                    .
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <Checkbox
                  checkboxId="understand-document"
                  isChecked={hasReadDisclosure}
                  handleChange={event => handleCheckedBox(event)}
                >
                  I have read and understand the above document
                </Checkbox>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12">
          <Button
            type="submit"
            className="button button--action__accept niso-button"
            isDisabled={isInvalid}
          >
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

EligiblePage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  initialHasReadDisclosure: PropTypes.bool.isRequired,
  isUSRelevant: PropTypes.bool.isRequired,
  isResidenceStateTexas: PropTypes.bool.isRequired,
  chosenSchool: PropTypes.shape({
    schoolName: PropTypes.string,
    universityName: PropTypes.string,
  }),
  chosenCourse: PropTypes.shape({
    label: PropTypes.string,
  }),
  chosenPeriod: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export { EligiblePage };

export const mapStateToProps = state => ({
  isUSRelevant: isUSRelevantSelector(state),
  isResidenceStateTexas: isResidenceStateTexasSelector(state),
  initialHasReadDisclosure: state.builder.ui.readDisclosureDocument,
  chosenSchool: selectedSchoolSelector(state),
  chosenCourse: selectedCourseSelector(state),
  chosenPeriod: periodSelector(state),
});

export const mapDispatchToProps = {
  dispatchWizardNav: navigateWizard,
  dispatchAddPageData: addPageData,
};

export default connect(mapStateToProps, mapDispatchToProps)(EligiblePage);
